import { useCallback, useContext, useMemo, useState } from "react";
import {
  CreateGuestTypeInput,
  CreateSeatTypeInput,
  CreateSeatsInput,
  Guest,
  GuestType,
  Price,
  Production,
  Reservation,
  Seat,
  SeatType,
  SetPriceListInput,
  Staff,
  Stage,
} from "@/graphql/types";
import { defaultSeatTypes } from "../common/commonObject";
import styled from "styled-components";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  InputLabel,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ResultBar from "../common/resultBar";
import LoadingIcon from "../common/loadingIcon";
import MyButton from "../common/button/button";
import { StageSettingCallbacks, DisplayedModal } from ".";
import EditSeatType from "./editSeatType";
import EditGuestType from "./editGuestType";
import EditPrice from "./editPrice";
import EditSeat from "./editSeats";
import AssignSeats from "./assignSeats";
import dayjs from "dayjs";
import Modal from "../common/modal/modal";
import Button from "../common/button/button";
import EditStage from "./editStage";
import { SEAT_STATES } from "../seat";
import { EMAIL_OPTION_VALUES, emailItemList, emailTargetList } from "@/lib/ts/admin/items";
import { classifyDevice } from "@/lib/ts/device";
import * as GraphQLClient from "@apollo/client"
import { GetAllSeatsOfStage, GetSeatsSettingsOfStage, UpdateSeats } from "@/graphql/documentNode";
import { useSnackBar } from "@/lib/ts/hooks/useSnackBar";
import { useQuery } from "react-query";
import { getDefaultSeatTypes } from "@/lib/ts/admin/defaultSeatTypes";
import SelectItemV2 from "../common/input/selectItemV2";

type Props = {
  productions: Production[];
  callbacks: StageSettingCallbacks;
  authToken: string;
  modalChange: (modalOpen: boolean) => void;
};

const SetupSeats = (props: Props) => {
  const device = classifyDevice()
  const snackBar = useSnackBar({})

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);

  const [productionId, setProductionId] = useState(
    undefined as undefined | number,
  );

  const [stages, setStages] = useState([] as Stage[]);
  const [stageReset, setStageReset] = useState(false);

  const [seats, setSeats] = useState([] as Seat[][]);
  const [seatsOfStage, setSeatsOfStage] = useState<Seat[]>([])

  const [seatTypes, setSeatTypes] = useState<SeatType[]>([])
  const [customSeatTypes, setCustomSeatTypes] = useState<SeatType[]>([]);

  const [stageId, setStageId] = useState(undefined as undefined | number);


  const [guestTypes, setGuestTypes] = useState<GuestType[]>([]);
  const [customGuestTypes, setCustomGuestTypes] = useState<GuestType[]>([]);

  const [prices, setPrices] = useState([] as Price[]);

  const [guests, setGuests] = useState([] as Guest[]);
  const [reservations, setReservations] = useState([] as Reservation[]);
  const [staffs, setStaffs] = useState([] as Staff[]);

  const [emailOption, setEmailOption] = useState(0)
  const [canSendEmail, setCanSendEmail] = useState(false)

  const [emailSubject, setEmailSubject] = useState("")
  const [emailText, setEmailText] = useState("")
  const [selectedEmailTargetIndex, setSelectedEmailTargetIndex] = useState(-1)
  
  const selectedEmailTarget = useMemo(() => {
    return emailTargetList[selectedEmailTargetIndex]?.value || 0
  }, [selectedEmailTargetIndex])

  const customEmailInput = useMemo(() => {
    if(emailOption === EMAIL_OPTION_VALUES.CUSTOM) {
      return {
        target: selectedEmailTarget,
        subject: emailSubject,
        text: emailText,
      }
    } else {
      return undefined
    }
  }, [emailOption, emailSubject, emailText, selectedEmailTarget])


  const isMobile = useMemo(() => {
    return device === "Android" || device === "ios" || device === "Mobile"
  }, [device])

  const [displayedModal, setDisplayedModal] = useState(
    undefined as undefined | DisplayedModal,
  );

  const selectedStage = useMemo(() => {
    console.log(stages.find((stage) => stage.id === stageId));
    return stages.find((stage) => stage.id === stageId);
  }, [stageId, stages]);

  const [getSeatsSettingsOfStage] = GraphQLClient.useLazyQuery(GetSeatsSettingsOfStage, {
    onCompleted: (data) => {
      if(data.getSeatsSettingsOfStage) {
        setSeatsOfStage(data.getSeatsSettingsOfStage.seats)
        setSeatTypes([
          ...getDefaultSeatTypes(stageId || -1),
          ...data.getSeatsSettingsOfStage.seatTypes
        ])
        setCustomSeatTypes(data.getSeatsSettingsOfStage.seatTypes)
        setGuests(data.getSeatsSettingsOfStage.guests)
        setGuestTypes([
          {
            id: 0,
            name: "一般",
            stageId,
          },
          ...data.getSeatsSettingsOfStage.guestTypes
        ])
        setCustomGuestTypes(data.getSeatsSettingsOfStage.guestTypes)
        setPrices(data.getSeatsSettingsOfStage.prices)
        setReservations(data.getSeatsSettingsOfStage.reservations)
        setStaffs(data.getSeatsSettingsOfStage.staffs)
      }
      snackBar.onQuerySuccess()
    },
    onError: (err) => {
      console.log(err)
      snackBar.onQueryError()
    },
    fetchPolicy: "network-only"
  })

  const getSettings = useCallback(async (stageId: number | undefined) => {
    await getSeatsSettingsOfStage({
      variables: {
        stageId,
      }
    })
  }, [getSeatsSettingsOfStage])

  useQuery([stageId], async () => {
    await getSettings(stageId)
  })


  const createSeatType = useCallback(
    async (input: CreateSeatTypeInput) => {
      await props.callbacks.createSeatTypeCallback(input);
      await getSettings(stageId);
    },
    [getSettings, props.callbacks, stageId],
  );

  const deleteSeatType = useCallback(
    async (id: number) => {
      await props.callbacks.deleteSeatTypeCallback(id);
      await getSettings(stageId);
    },
    [getSettings, props.callbacks, stageId],
  );

  const createGuestType = useCallback(
    async (input: CreateGuestTypeInput) => {
      await props.callbacks.createGuestTypeCallback(input);
      await getSettings(stageId);
    },
    [getSettings, props.callbacks, stageId],
  );

  const deleteGuestType = useCallback(
    async (id: number) => {
      await props.callbacks.deleteGuestTypeCallback(id);
      await getSettings(stageId);
    },
    [getSettings, props.callbacks, stageId],
  );

  const setPriceList = useCallback(
    async (input: SetPriceListInput) => {
      await props.callbacks.setPriceListCallback(input);
      await getSettings(stageId);
    },
    [getSettings, props.callbacks, stageId],
  );

  const createSeats = useCallback(
    async (input: CreateSeatsInput[][]) => {
      await props.callbacks.createSeatsCallback(input);
      await getSettings(stageId)
    },
    [getSettings, props.callbacks, stageId],
  );

  // 対象上演回が変更された場合の処理
  const stageChanged = useCallback(
    async (stageId: number) => {
      setStageId(stageId);
      await getSettings(stageId);
    },
    [getSettings],
  );

  const getAllStages = useCallback(
    async (productionId: number) => {
      const stages = productionId
        ? await props.callbacks.getAllStagesCallback(productionId)
        : [];
      setStages(stages);
      setStageReset(true);
      stageChanged(-1);
    },
    [props.callbacks, stageChanged],
  );

  // 対象公演が変更された場合の処理
  const productionChanged = useCallback(
    async (productionId: number) => {
      setProductionId(productionId);
      setSeats([]);
      setSeatTypes([]);
      await getAllStages(productionId);
    },
    [getAllStages],
  );

  const selectedProductionIndex = useMemo(() => {
    if(productionId) {
      return props.productions.findIndex(p => p.id === productionId)
    }
    return -1
  }, [productionId, props.productions])

  const selectedStageIndex = useMemo(() => {
    if(stageId) {
      return stages.findIndex(s => s.id === stageId)
    }
    return -1
  }, [stageId, stages])

  const selects = useMemo(
    () => (
      <div className="selects">
        {props.productions.length ? (
          <>
            <InputLabel>公演選択</InputLabel>
            <SelectItemV2 
              items={props.productions}
              selectedIndex={selectedProductionIndex}
              changeIndex={(production, index) => {
                productionChanged(production ? production.id : -1);
              }}
              width={250}
            />
          </>
        ) : (
          <></>
        )}
        {stages.length ? (
          <>
            <InputLabel>ステージ選択</InputLabel>
            <SelectItemV2 
              items={stages}
              labelName="stageName"
              selectedIndex={selectedStageIndex}
              changeIndex={(stage, index) => {
                setStageReset(false);
                stageChanged(stage.id);
              }}
              width={250}
            />
          </>
        ) : (
          <></>
        )}
      </div>
    ),
    [productionChanged, props.productions, selectedProductionIndex, selectedStageIndex, stageChanged, stages],
  );

  const onSeatTypeSubmit = useCallback(
    async (seatType: SeatType, isNew: boolean) => {
      setLoading(true);
      const input = {
        id: seatType.id,
        stageId: stageId!,
        name: seatType.name,
        description: seatType.description,
      };
      await createSeatType(input);
      setLoading(false);
    },
    [createSeatType, stageId],
  );

  const onSeatTypeDelete = useCallback(
    async (seatType: SeatType) => {
      if (seatType.id) {
        const id = seatType.id;
        await deleteSeatType(id);
      } else {
        setError(true);
      }
    },
    [deleteSeatType],
  );

  const onGuestTypeSubmit = useCallback(
    async (guestType: GuestType, isNew: boolean) => {
      setLoading(true);
      const input = {
        id: guestType.id,
        stageId: stageId!,
        name: guestType.name,
      };
      await createGuestType(input);
      setLoading(false);
    },
    [createGuestType, stageId],
  );

  const onGuestTypeDelete = useCallback(
    async (guestType: GuestType) => {
      if (guestType.id) {
        await deleteGuestType(guestType.id);
      } else {
        setError(true);
      }
    },
    [deleteGuestType],
  );

  const onPriceSubmit = useCallback(
    async (prices: Price[]) => {
      const input = {
        priceList: prices,
      };
      await setPriceList(input);
    },
    [setPriceList],
  );

  const [updateSeats] = GraphQLClient.useMutation(UpdateSeats, {
    onCompleted: (data) => {
      if(data.updateSeats.ok) {
        snackBar.onMutationSuccess()
      } else {
        console.log("err")
        snackBar.onMutationError()
      }
    },
    onError: (err) => {
      console.log(err)
      snackBar.onMutationError()
    },
  })

  const onSeatsSubmit = useCallback(
    async (seats: Seat[]) => {
      console.log(seats.length)
      snackBar.loadStart()
      await updateSeats({
        variables: {
          input: {
            stageId,
            seats: seats.map(seat => ({
              id: seat.id ?? -1,
              stageId: seat.stageId,
              seatName: seat.seatName,
              row: seat.row,
              column: seat.column,
              type: seat.type,
              state: seat.state ?? 0,
              locked: seat.locked ?? false
            }))
          }
        }
      })
      setDisplayedModal(undefined)
      getSettings(stageId)
    },
    [getSettings, snackBar, stageId, updateSeats],
  );

  const onStageEdited = useCallback(async (stage: Stage) => {
    await props.callbacks.updateStageSettingsCallback(stage)
    if(productionId) {
      await getAllStages(productionId)
    }
  }, [getAllStages, productionId, props.callbacks])

  const createAuthToken = useCallback(async () => {
    if (stageId) {
      await props.callbacks.createAuthTokenCallback(stageId);
    }
  }, [props.callbacks, stageId]);

  const reservationURL = useMemo(() => {
    const targetURL = `${process.env.REACT_APP_DOMAIN}/reservation/createReservation/${productionId}/${stageId}?serial=${props.authToken}`;
    const encodedURL = encodeURIComponent(targetURL)
    return `${process.env.REACT_APP_DOMAIN}?redirect=${encodedURL}`
  }, [productionId, props.authToken, stageId]);

  const onSeatsAssigned = useCallback(async (editedGuests: Guest[]) => {
    await props.callbacks.assignSeatsCallback(
      editedGuests.map((guest) => ({
        id: guest.id,
        guestType: guest.guestType,
        reservationId: guest.reservationId,
        seatId: guest.seatId,
      } as Guest))
    )
    if(stageId) {
      await stageChanged(stageId)
    }
  }, [props.callbacks, stageChanged, stageId])

  const reservationStarted = useMemo(() => {
    const stage = stages.find(stage => stage.id === stageId)
    if(!stage) {
      return true
    }

    return dayjs.unix(stage.reservationStart).isBefore(dayjs())
  }, [stageId, stages])

  const reservationExists = useMemo(() => {
    return seats.some(row => row.some(seat => (seat.state === SEAT_STATES.RESERVED || seat.state === SEAT_STATES.PAIRED) && seat.reservationId && seat.reservationId > -1))
  }, [seats])
  console.log(selectedStage)

  const selectedEmailOptionIndex = useMemo(() => {
    return emailItemList.findIndex(e => e.value === emailOption)
  }, [emailOption])


  const components = useMemo(() => {
    const body = (
      <>
        {selects}
        {stageId === undefined || stageId === -1 ? (
          <></>
        ) : (
          <div className="modal-buttons">
            <MyButton
              buttonType="powerful-red"
              label="座席種類設定"
              onClick={() => {
                setDisplayedModal("editSeatType");
                props.modalChange(true);
              }}
            />
            <MyButton
              buttonType="powerful-red"
              label="観客種類設定"
              onClick={() => {
                setDisplayedModal("editGuestType");
                props.modalChange(true);
              }}
            />
            <MyButton
              buttonType="powerful-red"
              label="価格設定"
              onClick={() => {
                setDisplayedModal("editPrice");
                props.modalChange(true);
              }}
            />
            <MyButton
              buttonType="powerful-red"
              label="座席設定"
              onClick={() => {
                setDisplayedModal("editSeats");
                props.modalChange(true);
              }}
            />
            {false ? (
              <MyButton
                buttonType="powerful-red"
                label="座席指定用URL発行"
                onClick={createAuthToken}
              />
            ) : (
              <></>
            )}
            {selectedStage ? 
              <MyButton
                  buttonType="powerful-red"
                  label={selectedStage.disableSeatSelect ? "座席割り振り" : "予約状況確認"}
                  onClick={() => {
                    setDisplayedModal("assignSeats");
                    props.modalChange(true);
                  }}
                  disabled={device !== "PC" && device !== "iPad"}
              /> : <></>}
            {selectedStage ? (
              <MyButton
                buttonType="powerful-red"
                label="ステージ削除"
                onClick={() => {
                  setDisplayedModal('deleteStage')
                }}
                disabled={reservationStarted}
              />
            ) : (
              <></>
            )}
            {selectedStage ? (
              <MyButton
                buttonType="powerful-red"
                label="予約状況出力(xlsx)"
                onClick={async () => {
                  await props.callbacks.createSpreadSheetCallback(stageId)
                }}
              />
            ) : (
              <></>
            )}
            {selectedStage && (
              <MyButton 
                buttonType="powerful-red"
                label="メール送信"
                onClick={() => {
                  setDisplayedModal("sendEmail")
                  props.modalChange(true)
                }}
              />
            )}
          </div>
        )}

        {props.authToken ? (
          <div className="reservation-url">
            予約用URL
            <br />
            <span className="url">{reservationURL}</span>
          </div>
        ) : (
          <></>
        )}
        {displayedModal === "editSeatType" && stageId !== undefined ? (
          <EditSeatType
            onClose={() => {
              setDisplayedModal(undefined);
              props.modalChange(false);
            }}
            onDelete={onSeatTypeDelete}
            onSubmit={onSeatTypeSubmit}
            seatTypes={customSeatTypes}
            stageId={stageId}
          />
        ) : (
          <></>
        )}
        {displayedModal === "editGuestType" && stageId !== undefined ? (
          <EditGuestType
            onClose={() => {
              setDisplayedModal(undefined);
              props.modalChange(false);
            }}
            onDelete={onGuestTypeDelete}
            onSubmit={onGuestTypeSubmit}
            guestTypes={customGuestTypes}
            stageId={stageId}
          />
        ) : (
          <></>
        )}
        {displayedModal === "editPrice" && stageId !== undefined ? (
          <EditPrice
            onClose={() => {
              setDisplayedModal(undefined);
              props.modalChange(false);
            }}
            stageId={stageId}
            guestTypes={guestTypes}
            seatTypes={seatTypes}
            prices={prices}
            onSubmit={onPriceSubmit}
          />
        ) : (
          <></>
        )}
        {displayedModal === "editSeats" && stageId !== undefined ? (
          <EditSeat
            seatTypes={seatTypes}
            prices={prices}
            onClose={() => {
              setDisplayedModal(undefined);
              props.modalChange(false);
            }}
            seats={seatsOfStage}
            onSubmit={onSeatsSubmit}
            stageId={stageId}
            device={device}
            reservationStarted={reservationStarted || reservationExists}
          />
        ) : (
          <></>
        )}
        {displayedModal === "assignSeats" && stageId !== undefined ? (
          <AssignSeats
            guests={guests}
            reservations={reservations}
            seatTypes={seatTypes}
            seats={seatsOfStage}
            staffList={staffs}
            onClose={() => {
              setDisplayedModal(undefined);
              props.modalChange(false);
            }}
            onSubmit={onSeatsAssigned}
          />
        ) : (
          <></>
        )}
        {displayedModal === "deleteStage" && stageId !== undefined ? (
          <Modal
            width={isMobile ? 320 : 1000}
            onClose={() => { setDisplayedModal(undefined) }}
            title="ステージ削除"
          >
            <>
              <div>このステージを削除してよろしいですか？</div>
              <div className="buttons">
                <div className="button">
                  <Button 
                    buttonType="powerful-red"
                    label="削除する"
                    onClick={() => {
                      const stage = stages.find(stage => stage.id === stageId)
                      if(stage) {
                        props.callbacks.deleteStageCallback({
                          id: stage.id,
                        })
                      }
                      setDisplayedModal(undefined)
                      if(productionId) {
                        productionChanged(productionId)
                      }
                    }}
                  />
                </div>
                <div className="button">
                  <Button 
                    buttonType="powerful-red"
                    label="キャンセル"
                    onClick={() => { setDisplayedModal(undefined) }}
                  />
                </div>
              </div>
            </>
          </Modal>
        ) : <></>}
        {
          displayedModal === "sendEmail" && stageId !== undefined && (
            <Modal title="メール送信" onClose={() => {
              setEmailOption(0)
              setCanSendEmail(false)
              setDisplayedModal(undefined)
            }}>
              <>
                <InputLabel>メール種類選択</InputLabel>
                <SelectItemV2 
                  items={emailItemList}
                  selectedIndex={selectedEmailOptionIndex}
                  changeIndex={(item, _) => {
                    setEmailOption(item.value)
                  }}
                  width={250}
                />
                {
                  emailOption === 3 && (
                    <>
                      <InputLabel>メール件名</InputLabel>
                      <TextField 
                        variant="standard"
                        onChange={(event) => { setEmailSubject(event.target.value) }}
                        sx={{
                          width: '100%',
                        }}
                      />
                    </>
                  )
                }
                {
                  emailOption === 3 && (
                    <>
                      <InputLabel>メール文面</InputLabel>
                      <TextField 
                        variant="outlined"
                        multiline
                        rows={5}
                        sx={{
                          width: '100%',
                          marginBottom: '8px'
                        }}
                        onChange={(event) => { setEmailText(event.target.value) }}
                      />
                    </>
                  )
                }
                {
                  emailOption === 3 &&(
                  <>
                    <InputLabel>送信対象選択</InputLabel>
                    <SelectItemV2 
                      items={emailTargetList}
                      selectedIndex={selectedEmailTargetIndex}
                      changeIndex={(_, index) => { setSelectedEmailTargetIndex(index) }}
                      width={250}
                    />
                  </>
                )}
                <p className="mail-caution">{emailOption ? emailItemList.find(item => item.value === emailOption)?.description : ""}</p>
                <div>上記内容でメールを一斉送信します。よろしいですか？</div>
                <div>
                  <Checkbox 
                    onClick={() => { setCanSendEmail(!canSendEmail) }} 
                    disabled={!emailOption}
                    defaultChecked={canSendEmail}
                  />
                  <span>はい、対象者全員にメールを送信します。</span>
                </div>
                <div>
                  <Button 
                    buttonType="powerful-red"
                    disabled={!canSendEmail || !emailOption}
                    label="メール送信"
                    onClick={async () => {
                      console.log(stageId)
                      setDisplayedModal(undefined)
                      setLoading(true)
                      await props.callbacks.sendEmailToStageGuestsCallback(stageId, emailOption, customEmailInput)
                      setEmailOption(0)
                      setCanSendEmail(false)
                      setLoading(false)
                    }}
                  />
                </div>
              </>
            </Modal>
          )
        }
        {displayedModal === 'editStage' && stageId ? (
          <EditStage 
            device={device!}
            onClose={() => setDisplayedModal(undefined)}
            stage={stages.find(stage => stage.id === stageId)!}
            updateStageSettingCallback={onStageEdited}
          />
        ) : <></>}
      </>
    );
    return device === "PC" || device === "iPad" ? (
      <SeatTypeStylePC>{body}</SeatTypeStylePC>
    ) : (
      <SeatTypeStyleMobile>{body}</SeatTypeStyleMobile>
    );
  }, [selects, stageId, createAuthToken, selectedStage, device, reservationStarted, props, reservationURL, displayedModal, onSeatTypeDelete, onSeatTypeSubmit, customSeatTypes, onGuestTypeDelete, onGuestTypeSubmit, customGuestTypes, guestTypes, seatTypes, prices, onPriceSubmit, seatsOfStage, onSeatsSubmit, reservationExists, guests, reservations, staffs, onSeatsAssigned, isMobile, selectedEmailOptionIndex, emailOption, selectedEmailTargetIndex, canSendEmail, stages, onStageEdited, productionId, productionChanged, customEmailInput]);

  const bars = useMemo(() => {
    return (
      <>
        {loading ? <LoadingIcon /> : <></>}
        <ResultBar
          setSubmitError={setError}
          failedMsg="処理に失敗しました。"
          setSubmitSucceeded={setSuccess}
          submitError={error}
          submitSucceeded={success}
          succeedMsg="正常に処理されました。"
        />
      </>
    );
  }, [error, loading, success]);

  return (
    <>
      <Accordion sx={{ borderRadius: "5px" }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography>ステージ設定</Typography>
        </AccordionSummary>
        <AccordionDetails>{components}</AccordionDetails>
      </Accordion>
      {bars}
    </>
  );
};

const SeatTypeStyleMobile = styled.div`
  .seatType {
    cursor: pointer;
    height: auto;
    width: 100%;
    display: flex;
    justify-content: left;
    gap: 20px;
    margin: 20px auto 20px auto;
    padding: 3px 10px 3px 10px;
    background-color: lightgray;
    border-radius: 5px;
    &.seatTypeInput {
      display: block;
      background: none;
    }
    &.onMode {
      background-color: #4a494c;
      color: white;
    }
    .seatTypeName {
      width: 150px;
      + input {
        width: 150px;
      }
    }
    .seatTypeDescription {
      width: 200px;
      + input {
        width: 200px;
      }
    }
  }
  .seatTypes {
    max-height: 300px;
    overflow-y: scroll;
  }

  .button {
    width: 120px;
    margin: auto;
  }
  .modal-buttons {
    display: grid;
    margin: 10px auto 10px auto;
    width: 244px;
    grid-template-columns: 122px 122px;
    grid-template-rows: 36.5px 36.5px;
    gap: 10px;
  }
  .reservation-url {
    margin: auto;
    width: 250px;
    .url {
      white-space: normal;
      overflow-wrap: break-word;
    }
  }
`;

const SeatTypeStylePC = styled.div`
  .selects {
    width: 250px;
    margin: auto;
  }
  .seatType {
    cursor: pointer;
    height: auto;
    width: 100%;
    display: flex;
    justify-content: left;
    gap: 20px;
    margin: 20px auto 20px auto;
    padding: 3px 10px 3px 10px;
    background-color: lightgray;
    border-radius: 5px;
    &.seatTypeInput {
      display: block;
      background: none;
    }
    &.onMode {
      background-color: #4a494c;
      color: white;
    }
    .seatTypeName {
      width: 150px;
      + input {
        width: 150px;
      }
    }
    .seatTypeDescription {
      width: 200px;
      + input {
        width: 200px;
      }
    }
  }
  .seatTypes {
    max-height: 300px;
    overflow-y: scroll;
  }

  .button {
    width: 120px;
    margin: auto;
  }
  .modal-buttons {
    display: grid;
    margin: 10px auto 10px auto;
    width: 390px;
    grid-template-columns: 130px 130px 130px;
    gap: 10px;
    align-items: center;
  }
  .reservation-url {
    margin: auto;
    width: 250px;
    .url {
      white-space: normal;
      overflow-wrap: break-word;
    }
  }
  .buttons {
    .button {
      margin: 10px auto 10px auto;
    }
  }
  .mail-caution {
    font-weight: 700;
  }
`;
export default SetupSeats;
